import * as React from "react";

import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";

import { Checkbox, Link, OutlinedInput, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import { produce } from "immer";
import {
  GetColumnGroups,
  GetFiltersVisibilityModel,
  ResetColumnHiddenToDefault,
  ToggleColumnShow,
} from "../../../Redux/Reducers/redux-slice-new-request";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import FilterAccordion from "../../Filters/FilterAccordion/filters-accordion";

import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DrawerWrapper from "../../Drawers/Drawer/drawer-wrapper";
import { useTranslation } from "react-i18next";

export interface NewRequestFilterPanelProps {
  open: boolean;
  onClose?: React.MouseEventHandler;
}

const NewRequestFilterPanel: React.FC<NewRequestFilterPanelProps> = (props) => {
  const { t } = useTranslation();
  const { open, onClose } = props;
  const dispatch = useAppDispatch();

  const [searchValue, setSearchValue] = React.useState("");
  const [expandArray, setExpandArray] = React.useState<boolean[]>([]);
  const columnsGroups = useAppSelector(GetColumnGroups);
  const filtersColumnVisibilityMask = useAppSelector(GetFiltersVisibilityModel);

  const toggleExpandByIndex = React.useCallback((groupIndex: number) => {
    setExpandArray(
      produce((draft) => {
        draft[groupIndex] = !draft[groupIndex];
      })
    );
  }, []);

  const getColumnIsShown = React.useCallback(
    (columnFieldName: string): boolean =>
      filtersColumnVisibilityMask.find((c) => c.columnFieldName === columnFieldName)?.show || false,
    [filtersColumnVisibilityMask]
  );

  const getNumberOfShownInGroup = React.useCallback(
    (groupName: string) =>
      filtersColumnVisibilityMask
        .filter((c) =>
          columnsGroups
            .find((g) => g.name === groupName)
            ?.columns.flatMap((gc) => gc.metadata.tagName!)
            .includes(c.columnFieldName)
        )
        .reduce((count, column) => (column.show ? ++count : count), 0),
    [columnsGroups, filtersColumnVisibilityMask]
  );

  React.useEffect(() => {
    setExpandArray(Array(columnsGroups.length).fill(false));
  }, [columnsGroups.length]);

  return (
    <DrawerWrapper
      width={440}
      header={<PanelHeader onClose={onClose as PanelHeaderProps["onClose"]} title={t("common:filters")} />}
      open={open}
      anchor="right"
      onClose={onClose}
    >
      <Stack pl={2} mb={1} spacing={2}>
        <OutlinedInput
          endAdornment={<SearchIcon />}
          sx={{ mr: 2 }}
          placeholder={t("field:placeholder.searchFilters")}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
        />
        <Link underline="hover" color="primary" onClick={() => dispatch(ResetColumnHiddenToDefault())}>
          <RefreshIcon sx={{ mr: 0.5 }} />
          {t("panel:newRequestFiltersPanel.resetToDefaultLink")}
        </Link>
      </Stack>
      {expandArray.length === columnsGroups.length &&
        columnsGroups
          .filter((g) => g.columns.some((c) => c.label.includesAllWords(searchValue)))
          .map((group, groupIndex) => (
            <FilterAccordion
              key={group.name}
              title={group.name}
              expanded={!searchValue.isEmpty() || expandArray[groupIndex]}
              onExpand={() => toggleExpandByIndex(groupIndex)}
              nbActiveFilters={getNumberOfShownInGroup(group.name)}
              noDivider={groupIndex + 1 === columnsGroups.length}
            >
              {group.columns
                .filter((c) => c.label.includesAllWords(searchValue))
                .map((column, columnIndex) => {
                  const show = getColumnIsShown(column.metadata.tagName!);
                  let color = "text.primary";
                  if (column.isMandatory) {
                    if (!show) color = "error.alphaHover";
                    else color = "error";
                  } else if (!show) {
                    color = "text.tier";
                  }
                  return (
                    <Stack key={columnIndex} direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body1" color={color}>
                        {column.label}
                      </Typography>
                      <Checkbox checked={show} onChange={() => dispatch(ToggleColumnShow(column.fieldId))} />
                    </Stack>
                  );
                })}
            </FilterAccordion>
          ))}
    </DrawerWrapper>
  );
};

export default NewRequestFilterPanel;
